import { Component, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSort } from "@angular/material/sort";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { NotificationService } from "src/app/notifications/notification.service";
import { PermissionService } from "src/app/permission.service";
import { SessionService } from "src/app/session.service";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { AccountDTO } from "../../accounts/Account";
import { RoleDTO } from "../Role";
import { FilterTableDialogComponent } from "../../shared/dialogs/filter-table-dialog/filter-table-dialog.component";
import { UiService } from "src/app/ui/ui.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { UtilService } from "src/app/util/util.service";

@Component({
  selector: "app-roles-list",
  templateUrl: "./roles-list.component.html",
  styleUrls: ["./roles-list.component.scss"],
})
export class RolesListComponent implements OnInit {
  account!: AccountDTO;
  roles!: any[];
  roleDataSource!: MatTableDataSource<any>;

  columns = [
    { code: "name", name: "Role Name", filter: true, selected: true },
    {
      code: "number_of_users",
      name: "# Members",
      filter: true,
      selected: true,
    },
    { code: "created_at", name: "Creation Date", filter: true, selected: true },
    {
      code: "created_by_name",
      name: "Created By",
      filter: true,
      selected: true,
    },
    {
      code: "modified_at",
      name: "Last Modified",
      filter: true,
      selected: true,
    },
    {
      code: "modified_by_name",
      name: "Modified By",
      filter: true,
      selected: true,
    },

    { code: "options" },
  ];
  displayedColumns = this.columns.map((column) => column.code);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("rolesTable") rolesTable!: MatTable<any[]>;
  searchValue: any;
  showEdit!: boolean;
  showNewRole!: boolean;
  showDelete!: boolean;
  showCopy!: boolean;
  private onComponentDestroy = new Subject();

  constructor(
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private PermissionService: PermissionService,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private router: Router,
    private uiService: UiService,
    private utilSvc: UtilService,
  ) {}

  ngOnInit(): void {
    this.roles = this.activatedRoute.snapshot.data.roles.filter(
      (role: any) => this.SessionService.isSuper || role.account_id,
    );
    this.roleDataSource = new MatTableDataSource(this.roles);

    this.roleDataSource.filterPredicate = (data: any, filter: string) => {
      let found = false;
      const selectedFilterColumns = this.columns
        .filter((column) => column.selected)
        .map((column) => column.code);
      for (let column of selectedFilterColumns) {
        let rowValue = data[column];
        if (typeof rowValue === "number") {
          rowValue = rowValue.toString();
        }
        if (rowValue) {
          if (rowValue?.trim().toLowerCase().indexOf(filter) !== -1) {
            found = true;
          }
        }
      }

      return found;
    };

    Promise.all([
      this.PermissionService.categories.roles.hasWrite(),
      this.PermissionService.categories.roles.hasDetail(),
      this.PermissionService.categories.permissions.hasList(),
      this.PermissionService.categories.roles.hasRemove(),
    ]).then(([hasWrite, hasDetail, hasPermissionList, hasRemove]) => {
      this.showEdit = hasWrite && hasDetail && hasPermissionList;
      this.showNewRole = hasWrite && hasPermissionList;
      this.showDelete = hasRemove;
      this.showCopy = hasWrite && hasDetail && hasPermissionList;
    });
  }

  ngAfterViewInit() {
    this.rolesTable.renderRows();
    this.roleDataSource.sort = this.sort;
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue;
  }

  createNewRole() {
    this.router.navigate(["create"], {
      relativeTo: this.activatedRoute.parent,
    });
  }

  formatDate = (date: Date) => {
    return this.utilSvc.formatDate(date);
  }

  applyFilter() {
    this.roleDataSource.filter = this.searchValue.trim().toLowerCase();
  }

  toggleFilterDialog() {
    this.dialog
      .open(FilterTableDialogComponent, {
        data: {
          columns: this.columns,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.columns = result;
          this.applyFilter();
        }
      });
  }

  copy(role: RoleDTO) {
    this.router.navigate(["create"], {
      relativeTo: this.activatedRoute.parent,
      state: { copyRole: role },
    });
  }

  edit(role: RoleDTO) {
    this.router.navigate([role.id], { relativeTo: this.activatedRoute.parent });
  }

  delete(row: any) {
    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Are you sure you want to delete this role ( ${row.name} )?`,
            warning: "Warning: this is not reversible!",
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.uiService
            .waitForEvent(
              this.PermissionService.deleteRole(
                this.currentAccount!.accountID,
                row.id,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              () => {
                this.NotificationService.openSuccessSnackBar("Role deleted");
                this.uiService
                  .waitForEvent(
                    this.PermissionService.getRoles(
                      this.currentAccount!.accountID,
                      true,
                    ),
                  )
                  .pipe(takeUntil(this.onComponentDestroy))
                  .subscribe((roles) => {
                    this.roles = (roles as any).filter(
                      (role: any) =>
                        this.SessionService.isSuper || role.account_id,
                    );
                    this.roleDataSource = new MatTableDataSource(this.roles);
                    this.rolesTable.renderRows();
                    this.roleDataSource.sort = this.sort;
                  });
              },
              () => {
                this.NotificationService.openErrorSnackBar(
                  "Error deleting role",
                );
              },
            );
        }
      });
  }
}
